import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PaperAirplaneIcon, AcademicCapIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { academyAPI } from '../../utils/api';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import QuizQuestion from '../../components/student/QuizQuestion';

// Pull a human message out of a 403 quota response (or any axios error).
const quotaMessage = (err) =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  'Something went wrong. Please try again.';

const StudyModule = () => {
  const { topicId } = useParams();
  const numericTopicId = Number(topicId);

  const [topic, setTopic] = useState(null);
  const [messages, setMessages] = useState([]); // { role:'student'|'tutor', content }
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [notice, setNotice] = useState('');

  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizAttempt, setQuizAttempt] = useState(null);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [numQuestions, setNumQuestions] = useState(5);

  const transcriptEndRef = useRef(null);

  // Find this topic's metadata (no single-topic endpoint, so list + find).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await academyAPI.getTopics();
        if (!active) return;
        const found = (res.data || []).find((t) => t.id === numericTopicId);
        setTopic(found || null);
      } catch {
        /* non-fatal — header just shows a generic title */
      }
    })();
    return () => { active = false; };
  }, [numericTopicId]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAsk = useCallback(async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || asking) return;
    setNotice('');
    setAsking(true);
    setMessages((prev) => [...prev, { role: 'student', content: q }]);
    setQuestion('');
    try {
      const res = await academyAPI.explain({
        topic_id: numericTopicId,
        question: q,
        ...(sessionId ? { session_id: sessionId } : {}),
      });
      const data = res.data || {};
      if (data.session_id) setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'tutor', content: data.answer || '', review: data.review }]);
    } catch (err) {
      const msg = quotaMessage(err);
      setNotice(msg);
      setMessages((prev) => [...prev, { role: 'tutor', content: `_${msg}_`, isError: true }]);
    } finally {
      setAsking(false);
    }
  }, [question, asking, numericTopicId, sessionId]);

  const handleGenerateQuiz = useCallback(async () => {
    if (generatingQuiz) return;
    setNotice('');
    setGeneratingQuiz(true);
    setQuizAttempt(null);
    setQuizAnswers({});
    try {
      const res = await academyAPI.generateQuiz({ topic_id: numericTopicId, num_questions: numQuestions });
      setQuiz(res.data || null);
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setGeneratingQuiz(false);
    }
  }, [generatingQuiz, numericTopicId, numQuestions]);

  const handleSubmitQuiz = useCallback(async () => {
    if (!quiz || submittingQuiz) return;
    setNotice('');
    setSubmittingQuiz(true);
    try {
      const res = await academyAPI.submitQuiz(quiz.id, quizAnswers);
      setQuizAttempt(res.data || null);
    } catch (err) {
      setNotice(quotaMessage(err));
    } finally {
      setSubmittingQuiz(false);
    }
  }, [quiz, quizAnswers, submittingQuiz]);

  // Map per-question feedback by question id for quick lookup.
  const feedbackById = {};
  if (quizAttempt && Array.isArray(quizAttempt.per_question)) {
    quizAttempt.per_question.forEach((pq) => { feedbackById[pq.question_id] = pq; });
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
      <Link to="/student/study" className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to study areas
      </Link>

      <div className="mb-6">
        <div className="flex items-center gap-2">
          <AcademicCapIcon className="h-6 w-6 text-primary-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {topic ? topic.name : 'Study topic'}
          </h1>
        </div>
        {topic?.summary && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{topic.summary}</p>}
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Educational study aid — not legal advice.</p>
      </div>

      {notice && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 p-3 rounded-md text-sm border border-amber-200 dark:border-amber-800">
          {notice}
        </div>
      )}

      {/* Tutor chat */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 mb-8">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-medium text-gray-900 dark:text-white">Ask your tutor</h2>
        </div>
        <div className="p-4 sm:p-5 space-y-4 max-h-[28rem] overflow-y-auto">
          {messages.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
              Ask anything about this topic — definitions, principles, leading cases, or how it applies.
            </p>
          ) : (
            messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === 'student' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-full sm:max-w-[85%] rounded-lg p-3 sm:p-4 ${
                    m.role === 'student'
                      ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                      : 'bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {m.role === 'student' ? (
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{m.content}</p>
                  ) : (
                    <div className="text-sm">
                      <MarkdownRenderer content={m.content} />
                      {m.review && typeof m.review.quality_score === 'number' && (
                        <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                          Reviewed · quality {m.review.quality_score}
                          {m.review.approved === false && ' · flagged for review'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {asking && (
            <div className="flex justify-start">
              <div className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-500 dark:text-gray-400">
                Thinking…
              </div>
            </div>
          )}
          <div ref={transcriptEndRef} />
        </div>
        <form onSubmit={handleAsk} className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question…"
            disabled={asking}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={asking || !question.trim()}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            <PaperAirplaneIcon className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Ask</span>
          </button>
        </form>
      </div>

      {/* Quiz */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-base font-medium text-gray-900 dark:text-white">Practice quiz</h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">Questions</label>
            <select
              value={numQuestions}
              onChange={(e) => setNumQuestions(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white"
            >
              {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              onClick={handleGenerateQuiz}
              disabled={generatingQuiz}
              className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {generatingQuiz ? 'Generating…' : quiz ? 'New quiz' : 'Generate quiz'}
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {!quiz ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Generate a quiz to test your understanding of this topic.
            </p>
          ) : (
            <>
              {quizAttempt && (
                <div className="mb-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">Your score</p>
                  <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{quizAttempt.score}%</p>
                </div>
              )}
              <div className="space-y-4">
                {(quiz.questions || []).map((qn) => (
                  <QuizQuestion
                    key={qn.id}
                    question={qn}
                    value={quizAnswers[qn.id]}
                    onChange={(val) => setQuizAnswers((prev) => ({ ...prev, [qn.id]: val }))}
                    result={feedbackById[qn.id]}
                    disabled={!!quizAttempt}
                  />
                ))}
              </div>
              {!quizAttempt && (
                <div className="mt-5">
                  <button
                    onClick={handleSubmitQuiz}
                    disabled={submittingQuiz}
                    className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {submittingQuiz ? 'Grading…' : 'Submit answers'}
                  </button>
                </div>
              )}
              {quiz.disclaimer && (
                <p className="mt-5 text-xs italic text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
                  {quiz.disclaimer}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudyModule;
